import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { getProfile, toggleBookmark } from '../services/api';
import ArchetypeBadge from './ArchetypeBadge';

const DIMENSION_LABELS = {
  rawSpeed: 'Speed', decisionQuality: 'Decision', aimPrecision: 'Aim',
  trackingAccuracy: 'Tracking', workingMemory: 'Memory', consistency: 'Consistency',
};

function ScoreCell({ value }) {
  const color = value >= 75 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
      <span style={{ fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </td>
  );
}

const BookmarkStarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function Shortlist() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareSet, setCompareSet] = useState(new Set());

  useEffect(() => {
    document.title = 'Shortlist | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);
  useEffect(() => {
    if (!user?.bookmarks?.length) {
      setLoading(false);
      return;
    }
    Promise.all(user.bookmarks.map((id) => getProfile(id).catch(() => null)))
      .then((results) => setProfiles(results.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [user]);

  const handleRemove = async (shareableId) => {
    await toggleBookmark(shareableId);
    await refreshUser();
    setProfiles((prev) => prev.filter((p) => p.shareableId !== shareableId));
    setCompareSet((prev) => { const n = new Set(prev); n.delete(shareableId); return n; });
  };

  const toggleCompare = (id) => {
    setCompareSet((prev) => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); return n; }
      if (n.size >= 2) return prev; // max 2
      n.add(id);
      return n;
    });
  };

  const getOverall = (scores) =>
    Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length);

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <div className="card" style={{ maxWidth: 400, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 8 }}>Sign in to view your shortlist</h2>
          <p className="text-muted">Your bookmarked profiles are saved to your account.</p>
        </div>
      </div>
    );
  }

  const compareIds = [...compareSet];

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{ borderBottom: '1px solid var(--border)', padding: '32px 0 24px' }}>
        <div className="container">
          <h1 style={{ marginBottom: 4 }}>My Shortlist</h1>
          <p className="text-muted">Bookmarked profiles saved to your account.</p>
        </div>
      </div>

      <div className="container" style={{ paddingTop: 24 }}>
        {loading ? (
          <p className="text-muted">Loading...</p>
        ) : profiles.length === 0 ? (
          <motion.div
            className="card"
            style={{ maxWidth: 440, textAlign: 'center' }}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, color: 'var(--text-muted)' }}>
              <BookmarkStarIcon />
            </div>
            <h2 style={{ marginBottom: 8 }}>No bookmarks yet</h2>
            <p className="text-muted" style={{ marginBottom: 20 }}>
              Visit a player's profile page and click "Bookmark" to add them to your shortlist.
            </p>
            <button className="btn btn--primary" onClick={() => navigate('/scout')}>
              Browse Scout Dashboard
            </button>
          </motion.div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Table header bar */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                {profiles.length} bookmarked player{profiles.length !== 1 ? 's' : ''}
              </span>
              <AnimatePresence>
                {compareSet.size === 2 ? (
                  <motion.button
                    key="compare-btn"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    className="btn btn--primary"
                    style={{ fontSize: 13, padding: '7px 20px' }}
                    onClick={() => navigate(`/compare?a=${compareIds[0]}&b=${compareIds[1]}`)}
                  >
                    Compare Selected →
                  </motion.button>
                ) : compareSet.size === 1 ? (
                  <motion.span
                    key="compare-hint"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ fontSize: 13, color: 'var(--text-muted)' }}
                  >
                    Select one more to compare
                  </motion.span>
                ) : (
                  <motion.span
                    key="compare-idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ fontSize: 12, color: 'var(--text-muted)' }}
                  >
                    Select 2 players to compare
                  </motion.span>
                )}
              </AnimatePresence>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '10px 16px', width: 40 }} />
                    <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 1 }}>PLAYER</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 1 }}>ARCHETYPE</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700 }}>OVERALL</th>
                    {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
                      <th key={key} style={{ padding: '10px 8px', textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700 }}>{label.toUpperCase()}</th>
                    ))}
                    <th style={{ padding: '10px 16px', width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((profile, i) => {
                    const checked = compareSet.has(profile.shareableId);
                    const disabled = !checked && compareSet.size >= 2;
                    return (
                      <motion.tr
                        key={profile.shareableId}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          background: checked ? 'rgba(0,212,255,0.04)' : 'transparent',
                          transition: 'background 0.12s',
                          opacity: disabled ? 0.45 : 1,
                        }}
                        onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = checked ? 'rgba(0,212,255,0.07)' : 'var(--bg-card-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = checked ? 'rgba(0,212,255,0.04)' : 'transparent'; }}
                      >
                        {/* Compare checkbox */}
                        <td style={{ padding: '12px 16px' }}>
                          <button
                            onClick={() => toggleCompare(profile.shareableId)}
                            disabled={disabled}
                            title={disabled ? 'Deselect another to add this one' : checked ? 'Remove from comparison' : 'Add to comparison'}
                            style={{
                              width: 20, height: 20, borderRadius: 5,
                              border: `2px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                              background: checked ? 'var(--accent)' : 'transparent',
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.15s', flexShrink: 0,
                              padding: 0,
                            }}
                          >
                            {checked && (
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        </td>

                        <td
                          style={{ padding: '12px 8px', cursor: 'pointer' }}
                          onClick={() => navigate(`/profile/${profile.shareableId}`)}
                        >
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{profile.playerTag}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{profile.shareableId}</div>
                        </td>
                        <td style={{ padding: '12px 8px', cursor: 'pointer' }} onClick={() => navigate(`/profile/${profile.shareableId}`)}>
                          <ArchetypeBadge archetypeId={profile.archetype.id} archetypeName={profile.archetype.name} />
                        </td>
                        <ScoreCell value={getOverall(profile.scores)} />
                        {Object.keys(DIMENSION_LABELS).map((key) => (
                          <ScoreCell key={key} value={profile.scores[key] || 0} />
                        ))}
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleRemove(profile.shareableId)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', fontSize: 16, lineHeight: 1, padding: 4 }}
                            title="Remove bookmark"
                          >
                            ★
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
